import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PillBadge } from "./Primitives";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import {
  ResponsiveContainer,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine,
} from "recharts";
import {
  Activity, TrendingUp, TrendingDown, ChevronRight, ArrowRight, Info,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import SiteScorecardModal from "./SiteScorecardModal";

/**
 * SitePerformanceCard — visual site-performance leaderboard for CSO/OM.
 *
 * Pulls `/metrics/site-performance` and ranks sites by a composite
 * "coverage-quality" score computed server-side:
 *   coverage_score = 0.60 * on_time_rate
 *                  + 0.25 * reliability_rate  (1 - missed/early-out density)
 *                  + 0.15 * fence_rate        (1 - breach density)
 *
 * Volume metrics (incidents, MRs, tour scans) are INTENTIONALLY excluded
 * from the ranking — those are counted separately on the ops scorecard,
 * this card is strictly about whether a site is properly covered.
 *
 * Every row also carries `prev_coverage_score` + `score_delta_pct` for
 * the ↑/↓ DeltaChip vs the immediately-preceding window of equal length.
 */

const TOOLTIP_CURSOR = { fill: "#FF3B3010" };
const BAR_MAX_ROWS = 20;   // how many bars to render on the main chart
const BAND_OPTIONS = [
  { key: "all",   label: "All bands",       description: "Every scored site" },
  { key: "best",  label: "Best (≥ 85)",     description: "Green band only" },
  { key: "worst", label: "Worst (< 55)",    description: "Red band only" },
];

function scoreTone(score) {
  if (score == null) return "#3F3F46";
  if (score >= 85) return "#34C759";
  if (score >= 70) return "#0A84FF";
  if (score >= 55) return "#FF9F0A";
  return "#FF3B30";
}

/**
 * DeltaChip — small ↑ / ↓ badge showing the percentage change vs the
 * previous window of equal length.
 *   • null (no prior window data) → "NEW" pill
 *   • > 0                          → green ↑
 *   • < 0                          → red ↓
 *   • == 0                         → neutral em-dash chip
 */
function DeltaChip({ pct, testid }) {
  if (pct === null || pct === undefined) {
    return (
      <span
        data-testid={testid}
        className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 border border-[#27272A] px-1.5 py-0.5"
        title="No prior window data"
      >
        NEW
      </span>
    );
  }
  const val = Number(pct);
  if (Math.abs(val) < 0.05) {
    return (
      <span
        data-testid={testid}
        className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 border border-[#27272A] px-1.5 py-0.5"
        title="No change vs previous window"
      >
        — 0%
      </span>
    );
  }
  const positive = val > 0;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-0.5 text-[10px] font-mono tabular-nums px-1.5 py-0.5 border ${
        positive
          ? "text-[#34C759] border-[#34C7594D]"
          : "text-[#FF3B30] border-[#FF3B304D]"
      }`}
      title={`${positive ? "Up" : "Down"} ${Math.abs(val).toFixed(1)}% vs previous window`}
    >
      {positive ? "▲" : "▼"} {Math.abs(val).toFixed(1)}%
    </span>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] px-3 py-2 text-xs text-zinc-200 font-mono">
      <div className="font-display font-bold uppercase tracking-wide mb-1">
        {row.site_name}
      </div>
      {row.client_name && (
        <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">
          {row.client_name}
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div className="text-zinc-500">Score</div>
        <div className="text-right" style={{ color: scoreTone(row.coverage_score) }}>
          {row.coverage_score ?? "—"} / 100
        </div>
        <div className="text-zinc-500">On-time</div>
        <div className="text-right">{row.on_time_rate ?? "—"}%</div>
        <div className="text-zinc-500">Reliability</div>
        <div className="text-right">{row.reliability_rate ?? "—"}%</div>
        <div className="text-zinc-500">Fence</div>
        <div className="text-right">{row.fence_rate ?? "—"}%</div>
        <div className="text-zinc-500">Shifts</div>
        <div className="text-right">{row.total_shifts ?? 0}</div>
      </div>
    </div>
  );
}

export default function SitePerformanceCard({
  range,
  rangeLabel = "This Month",
  cadence = "7d",
  onCadenceChange,
}) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);

  // Filter state — driven by the new filter bar. Client + Portfolio go to
  // the backend as query params; site + band are applied client-side on
  // the returned rankings so users can slice a single request further.
  const [clientFilter, setClientFilter] = useState("all");        // client_name or "all"
  const [portfolioFilter, setPortfolioFilter] = useState("all");  // portfolio_id or "all"
  const [siteIds, setSiteIds] = useState([]);                     // multi-select site_id[]
  const [bandFilter, setBandFilter] = useState("all");            // "all" | "best" | "worst"

  const [drillSiteId, setDrillSiteId] = useState(null);
  const { theme } = useTheme() || { theme: "dark" };

  // Reference data — clients (with portfolios) + sites — used to populate
  // the filter dropdown options and to derive linked scoping.
  const [clients, setClients] = useState([]);
  const [sitesRef, setSitesRef] = useState([]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => setClients([]));
    api.get("/sites").then((r) => setSitesRef(r.data || [])).catch(() => setSitesRef([]));
  }, []);

  // Imperative cascading resets folded into the handlers (avoid an extra
  // fetch cycle from declarative useEffect-based resets on mount).
  const handleClientChange = (v) => {
    setClientFilter(v);
    setPortfolioFilter("all");
    setSiteIds([]);
  };
  const handlePortfolioChange = (v) => {
    setPortfolioFilter(v);
    setSiteIds([]);
  };

  // Fetch site-performance for the current window + backend filters.
  // Uses AbortController so rapid filter changes don't produce stale
  // responses racing to setState.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const params = {};
        if (range?.start) params.start = range.start;
        if (range?.end) params.end = range.end;
        if (clientFilter !== "all") params.client_name = clientFilter;
        if (portfolioFilter !== "all") params.portfolio_id = portfolioFilter;
        const r = await api.get("/metrics/site-performance", {
          params,
          signal: controller.signal,
        });
        setRows(r.data?.sites || []);
        setErr(false);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        setErr(true);
      }
    })();
    return () => controller.abort();
  }, [range?.start, range?.end, clientFilter, portfolioFilter]);

  // ── Options for the filter dropdowns ────────────────────────────────
  const clientOptions = useMemo(() => {
    const names = Array.from(new Set((clients || []).map((c) => c.company_name).filter(Boolean)));
    names.sort();
    return names;
  }, [clients]);

  // Portfolios come from the picked client's `portfolios[]` (list of
  // {id, name}). When "all clients" is selected we surface the union.
  const portfolioOptions = useMemo(() => {
    let source = clients || [];
    if (clientFilter !== "all") source = source.filter((c) => c.company_name === clientFilter);
    const out = [];
    for (const c of source) {
      for (const p of (c.portfolios || [])) {
        if (p?.id && p?.name) out.push({ id: p.id, name: p.name, client_name: c.company_name });
      }
    }
    return out;
  }, [clients, clientFilter]);

  const siteOptions = useMemo(() => {
    let list = sitesRef || [];
    if (clientFilter !== "all") list = list.filter((s) => s.client_name === clientFilter);
    if (portfolioFilter !== "all") list = list.filter((s) => s.portfolio_id === portfolioFilter);
    return list
      .map((s) => ({ value: s.id, label: s.name, sub: s.client_name || undefined }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sitesRef, clientFilter, portfolioFilter]);

  // Only rank sites that actually had shifts in the window.
  const ranked = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    let base = rows.filter((s) => s.coverage_score != null && (s.total_shifts || 0) > 0);
    // Site multi-select is applied here (backend endpoint only takes one).
    if (siteIds.length > 0) {
      const wanted = new Set(siteIds);
      base = base.filter((s) => wanted.has(s.site_id));
    }
    if (bandFilter === "best") {
      base = base.filter((s) => (s.coverage_score || 0) >= 85);
    } else if (bandFilter === "worst") {
      base = base.filter((s) => (s.coverage_score || 0) < 55);
    }
    return base.sort((a, b) => (b.coverage_score || 0) - (a.coverage_score || 0));
  }, [rows, siteIds, bandFilter]);

  const avg = useMemo(() => {
    if (!ranked.length) return null;
    const total = ranked.reduce((s, r) => s + (r.coverage_score || 0), 0);
    return Math.round(total / ranked.length);
  }, [ranked]);

  const bestTen = useMemo(() => ranked.slice(0, 10), [ranked]);
  const worstTen = useMemo(
    () => ranked.slice().sort((a, b) => (a.coverage_score || 0) - (b.coverage_score || 0)).slice(0, 10),
    [ranked],
  );

  // Chart — always worst-first, capped at BAR_MAX_ROWS. The single-site
  // sub-score view was removed in favour of the scorecard drill modal.
  const chartData = useMemo(() =>
    ranked
      .slice()
      .sort((a, b) => (a.coverage_score || 0) - (b.coverage_score || 0))
      .slice(0, BAR_MAX_ROWS),
  [ranked]);

  const gridStroke = theme === "light" ? "#ececee" : "#27272A";
  const axisStroke = theme === "light" ? "#a1a1aa" : "#71717A";

  const anyFilterActive =
    clientFilter !== "all" || portfolioFilter !== "all" || siteIds.length > 0 || bandFilter !== "all";
  const resetFilters = () => {
    setClientFilter("all");
    setPortfolioFilter("all");
    setSiteIds([]);
    setBandFilter("all");
  };

  return (
    <section
      data-testid="dashboard-site-performance-card"
      className="border border-[#27272A] bg-[#121212]"
    >
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-mute flex items-center gap-2">
            <Activity className="size-3.5" />
            Coverage Quality · {rangeLabel}
            <span
              className="group relative inline-flex ml-1"
              data-testid="site-performance-score-info"
            >
              <Info className="size-3 text-zinc-500 hover:text-zinc-200 cursor-help transition-colors" />
              <span className="absolute left-0 top-5 z-20 hidden group-hover:block w-[300px] p-3 border border-[#27272A] bg-[#0A0A0A] text-[11px] normal-case tracking-normal text-zinc-300 shadow-2xl">
                <span className="block font-display font-bold uppercase tracking-widest text-[10px] text-zinc-400 mb-1.5">
                  How this score works
                </span>
                Normalized 0–100 (higher = better).<br />
                <span className="text-zinc-400">Formula:</span>{" "}
                <span className="font-mono">0.60·on-time + 0.25·reliability + 0.15·fence</span>
                <span className="block mt-2 space-y-0.5">
                  <span className="block"><span className="inline-block size-2 bg-[#34C759] mr-1.5" />≥85 Excellent</span>
                  <span className="block"><span className="inline-block size-2 bg-[#0A84FF] mr-1.5" />70–84 Healthy</span>
                  <span className="block"><span className="inline-block size-2 bg-[#FF9F0A] mr-1.5" />55–69 Watch</span>
                  <span className="block"><span className="inline-block size-2 bg-[#FF3B30] mr-1.5" />&lt;55 Under-performing</span>
                </span>
                <span className="block mt-2 text-zinc-400">
                  Delta chips show <span className="text-[#34C759]">▲</span> /{" "}
                  <span className="text-[#FF3B30]">▼</span> percentage change vs the
                  previous window of equal length.
                </span>
              </span>
            </span>
          </div>
          <div className="font-display uppercase font-bold text-xl mt-1">
            Site Performance
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {avg != null && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Avg. score{" "}
              <span
                className="font-mono font-bold tabular-nums text-sm"
                style={{ color: scoreTone(avg) }}
              >
                {avg}
              </span>{" "}
              / 100
            </div>
          )}
          <Link
            to="/reports?tab=site-performance"
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] flex items-center gap-1"
            data-testid="site-performance-view-all"
          >
            Full scorecard <ArrowRight className="size-3" />
          </Link>
        </div>
      </div>

      {/* Filter bar — Range · Client · Portfolio · Sites · Band */}
      <div
        className="px-5 py-3 border-b border-[#27272A] flex flex-wrap items-center gap-3"
        data-testid="site-performance-filter-bar"
      >
        {/* Range picker (7D / 30D / 90D) — writes to the global cadence
            so OpsHealthBadge + Reports at a Glance stay in sync. */}
        <div
          role="radiogroup"
          aria-label="Metrics window"
          className="inline-flex items-center gap-1"
          data-testid="site-performance-range-picker"
        >
          {["7d", "30d", "90d"].map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={cadence === c}
              data-testid={`site-performance-range-${c}`}
              onClick={() => onCadenceChange && onCadenceChange(c)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-widest border transition-colors ${
                cadence === c
                  ? "text-white border-[#0A84FF]"
                  : "border-[#27272A] text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {c === "7d" ? "7D" : c === "30d" ? "30D" : "90D"}
            </button>
          ))}
        </div>

        <FilterSelect
          testid="site-performance-client-filter"
          label="Client"
          value={clientFilter}
          onChange={handleClientChange}
          options={[
            { value: "all", label: "All clients" },
            ...clientOptions.map((n) => ({ value: n, label: n })),
          ]}
        />

        <FilterSelect
          testid="site-performance-portfolio-filter"
          label="Portfolio"
          value={portfolioFilter}
          onChange={handlePortfolioChange}
          options={[
            { value: "all", label: portfolioOptions.length ? "All portfolios" : "No portfolios" },
            ...portfolioOptions.map((p) => ({
              value: p.id,
              label: p.name,
              sub: clientFilter === "all" ? p.client_name : undefined,
            })),
          ]}
          disabled={portfolioOptions.length === 0}
        />

        <MultiSelectDropdown
          testId="site-performance-sites-filter"
          label={siteIds.length ? `${siteIds.length} site${siteIds.length === 1 ? "" : "s"}` : "All sites"}
          options={siteOptions}
          selected={siteIds}
          onChange={setSiteIds}
          triggerClassName="bg-[#0A0A0A] border border-[#27272A] text-xs text-zinc-200 px-2 py-1 hover:border-zinc-500 h-[26px] min-w-[110px]"
          emptyHint="No sites for the current client/portfolio"
        />

        {/* Band show-only toggle — All / Best (≥85) / Worst (<55) */}
        <div
          role="radiogroup"
          aria-label="Score band"
          className="inline-flex items-center gap-1"
          data-testid="site-performance-band-filter"
        >
          {BAND_OPTIONS.map((b) => (
            <button
              key={b.key}
              type="button"
              role="radio"
              aria-checked={bandFilter === b.key}
              data-testid={`site-performance-band-${b.key}`}
              title={b.description}
              onClick={() => setBandFilter(b.key)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-widest border transition-colors ${
                bandFilter === b.key
                  ? b.key === "best"
                    ? "text-white border-[#34C759]"
                    : b.key === "worst"
                      ? "text-white border-[#FF3B30]"
                      : "text-white border-[#0A84FF]"
                  : "border-[#27272A] text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>

        {anyFilterActive && (
          <button
            type="button"
            onClick={resetFilters}
            className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
            data-testid="site-performance-reset-filters"
          >
            Reset filters
          </button>
        )}
      </div>

      <div className="divide-y divide-[#27272A]">
        {/* Chart */}
        <div className="p-5" data-testid="site-performance-chart-wrap">
          {rows === null && !err && (
            <div className="text-zinc-500 text-sm py-8 text-center scanlines">
              Loading site performance…
            </div>
          )}
          {err && (
            <div className="text-zinc-500 text-sm py-8 text-center">
              Metrics unavailable.
            </div>
          )}
          {rows !== null && ranked.length === 0 && !err && (
            <div
              data-testid="site-performance-empty"
              className="flex flex-col items-center gap-3 text-zinc-400 text-sm py-10"
            >
              {anyFilterActive ? (
                <>
                  <PillBadge tone="amber">NO MATCHES</PillBadge>
                  No sites match the current filters.
                  <button
                    onClick={resetFilters}
                    className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
                  >
                    Reset filters
                  </button>
                </>
              ) : (
                <>
                  <PillBadge tone="green">CLEAR</PillBadge>
                  No shifts logged in this window — no sites to rank yet.
                </>
              )}
            </div>
          )}
          {ranked.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 flex items-center justify-between gap-3 flex-wrap">
                <span>
                  {`Ranking ${Math.min(chartData.length, ranked.length)} of ${ranked.length} scored sites · worst-first · click any bar for details`}
                </span>
              </div>
              <div className="w-full" style={{ height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 12, left: 0, bottom: 44 }}
                  >
                    <CartesianGrid stroke={gridStroke} vertical={false} />
                    <XAxis
                      type="category"
                      dataKey="site_name"
                      stroke={axisStroke}
                      fontSize={10}
                      interval={0}
                      angle={-32}
                      textAnchor="end"
                      height={44}
                      tick={{ fill: axisStroke }}
                    />
                    <YAxis
                      type="number"
                      domain={[0, 100]}
                      stroke={axisStroke}
                      fontSize={10}
                      tickCount={6}
                      width={32}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={TOOLTIP_CURSOR} />
                    <ReferenceLine y={85} stroke="#34C759" strokeDasharray="3 3" opacity={0.35} />
                    <ReferenceLine y={70} stroke="#0A84FF" strokeDasharray="3 3" opacity={0.35} />
                    <ReferenceLine y={55} stroke="#FF9F0A" strokeDasharray="3 3" opacity={0.35} />
                    <Bar
                      dataKey="coverage_score"
                      radius={[3, 3, 0, 0]}
                      cursor="pointer"
                      onClick={(payload) => {
                        if (payload?.site_id) setDrillSiteId(payload.site_id);
                      }}
                    >
                      {chartData.map((entry, i) => (
                        <Cell key={`bar-${entry.site_id}-${i}`} fill={scoreTone(entry.coverage_score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2 bg-[#34C759]" /> ≥85 Excellent
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2 bg-[#0A84FF]" /> 70–84 Healthy
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2 bg-[#FF9F0A]" /> 55–69 Watch
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2 bg-[#FF3B30]" /> &lt;55 Under-performing
                </span>
              </div>
            </>
          )}
        </div>

        {/* Best / Worst lists — side by side below the chart */}
        {ranked.length > 0 && (
          <div
            className="p-5 grid md:grid-cols-2 gap-6"
            data-testid="site-performance-rankings"
          >
            <RankList
              title="Best · Top 10"
              icon={TrendingUp}
              accent="#34C759"
              rows={bestTen}
              testIdPrefix="site-performance-best"
              emptyLabel="No ranked sites yet."
              onDrill={setDrillSiteId}
            />
            <RankList
              title="Worst · Top 10"
              icon={TrendingDown}
              accent="#FF3B30"
              rows={worstTen}
              testIdPrefix="site-performance-worst"
              emptyLabel="No ranked sites yet."
              onDrill={setDrillSiteId}
            />
          </div>
        )}
      </div>

      {drillSiteId && (
        <SiteScorecardModal
          siteId={drillSiteId}
          range={range}
          onClose={() => setDrillSiteId(null)}
        />
      )}
    </section>
  );
}

function RankList({ title, icon: Icon, accent, rows, testIdPrefix, emptyLabel, onDrill }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 5);
  const remainder = rows.length - visible.length;
  return (
    <div>
      <div className="label-mute flex items-center gap-2 mb-2" style={{ color: accent }}>
        <Icon className="size-3.5" />
        {title}
        <span className="text-zinc-500 font-mono text-[10px] ml-auto tabular-nums">
          {rows.length}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-500 py-2">{emptyLabel}</div>
      ) : (
        <>
          <ol className="space-y-1.5">
            {visible.map((s, idx) => (
              <li
                key={s.site_id}
                data-testid={`${testIdPrefix}-row-${s.site_id}`}
                className="flex items-center gap-2"
              >
                <span className="text-[10px] font-mono text-zinc-500 tabular-nums w-4 text-right">
                  {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => onDrill && onDrill(s.site_id)}
                  className="flex-1 min-w-0 flex items-center gap-2 hover:bg-[#1c1c1e] px-2 py-1 -mx-2 transition-colors group text-left"
                  data-testid={`${testIdPrefix}-drill-${s.site_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-zinc-100 truncate group-hover:text-white">
                      {s.site_name}
                    </div>
                    {s.client_name && (
                      <div className="text-[10px] text-zinc-500 truncate">
                        {s.client_name}
                      </div>
                    )}
                  </div>
                  <DeltaChip
                    pct={s.score_delta_pct}
                    testid={`${testIdPrefix}-delta-${s.site_id}`}
                  />
                  <div
                    className="text-xs font-mono font-bold tabular-nums shrink-0 w-8 text-right"
                    style={{ color: scoreTone(s.coverage_score) }}
                  >
                    {s.coverage_score ?? "—"}
                  </div>
                  <ChevronRight className="size-3 text-zinc-600 group-hover:text-zinc-300 transition-colors shrink-0" />
                </button>
              </li>
            ))}
          </ol>
          {rows.length > 5 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 transition-colors"
              data-testid={`${testIdPrefix}-toggle`}
            >
              {expanded
                ? "Show less"
                : remainder > 0
                  ? `View ${remainder} more →`
                  : "View more →"}
            </button>
          )}
        </>
      )}
    </div>
  );
}


/**
 * FilterSelect — compact styled <select> matching the filter-bar aesthetic.
 * Used for Client + Portfolio (single-select). Site is multi-select via
 * MultiSelectDropdown so it lives outside this helper.
 */
function FilterSelect({ testid, label, value, onChange, options, disabled }) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <select
        data-testid={testid}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`bg-[#0A0A0A] border border-[#27272A] text-xs text-zinc-200 px-2 py-1 h-[26px] max-w-[180px] focus:outline-none focus:border-[#FF3B30] ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}{o.sub ? ` — ${o.sub}` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}
